import React, { useEffect } from 'react';
import { v4 as uuid } from 'uuid';
import { EventModal } from 'containers/CahierJournal/EventModal';
import { useCreateCJSeances } from '../queries/useCreateCJSeances';
import Dialog from '@teetsh/app/src/components/Dialog';
import { useIsBelowBreakpoint } from '@teetsh/app/src/components/ResponsiveSwitcher/useIsBelowSize';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import getFirstTagOfClassrooms from '@teetsh/app/src/utils/getFirstTagOfClassrooms';
import { getCycleFromClassrooms } from '../EventModal/getCycleFromClassrooms';
import { useAtom } from 'jotai';
import { creatorCJSeancePayloadAtom } from '../CreateCJSeanceModal/atom';
import { logNetworkError } from '@teetsh/app/src/utils/errors';
import { useTranslation } from 'react-i18next';
import { useUpdateCJSeance } from 'containers/CahierJournal/queries/useUpdateCJSeance';
import { CJEvent } from 'types/event';
import { trackEvent } from 'queries/user/track';
import { useDraftCJSeance, useResetDraftCJSeance } from '../atoms';
import useClassrooms from 'containers/App/context/ClassroomsContext/useClassrooms';

export interface Props {
  onClose: () => void;
  isOpen: boolean;
}

export function CreateCJSeanceModal() {
  const createEvents = useCreateCJSeances();
  const { notifyError } = useNotification();
  const [editorPayload, setPayload] = useAtom(creatorCJSeancePayloadAtom);
  const isMobile = useIsBelowBreakpoint('md');
  const draftCJSeance = useDraftCJSeance();
  const resetDraftCJSeance = useResetDraftCJSeance();
  const allClassrooms = useClassrooms();

  useEffect(() => {
    const hasDraftInCreationMode = draftCJSeance && !draftCJSeance.id;
    if (hasDraftInCreationMode && !editorPayload) {
      trackEvent('CahierJournal:resumeCreateCJSeance');
      setPayload({
        classrooms: allClassrooms.filter(cl =>
          draftCJSeance.classroomIds.includes(cl.id),
        ),
        seance: draftCJSeance,
      });
    }
  }, [draftCJSeance]);

  useEffect(
    () => () => {
      setPayload(undefined);
    },
    [],
  );
  const classrooms = editorPayload?.classrooms || [];
  const onClose = () => {
    resetDraftCJSeance();
    setPayload(undefined);
  };
  const updateEvent = useUpdateCJSeance();
  const { t } = useTranslation(['cahierJournal']);

  return (
    <Dialog
      full={isMobile}
      isOpen={!!editorPayload}
      size="2xl"
      onClose={onClose}
      position="top"
    >
      <EventModal
        allClassrooms={classrooms}
        closeModal={onClose}
        onSubmit={values => {
          if (values.id) {
            return updateEvent({
              event: values,
              previousDay: values.start,
            });
          }
          return createEvents({
            cjEvents: [
              {
                ...values,
                id: uuid(),
              },
            ],
          })
            .then(v => {
              if ((v || []).length > 0) {
                return v[0];
              }
              return undefined;
            })
            .catch(err => {
              logNetworkError(err, "Couldn't create cj event from modal");
              notifyError({
                title: t('cahierJournal:messages.seanceNotSaved'),
              });
            });
        }}
        event={{
          ...(editorPayload?.seance as CJEvent),
          classroomIds: classrooms.map(c => c.id),
          cycle: getCycleFromClassrooms(classrooms),
          tag: getFirstTagOfClassrooms(classrooms),
        }}
      />
    </Dialog>
  );
}
